import { z } from 'zod';
import { pageSchema, resourceKeySchema, timestampSchema } from './common';

export const projectStatusSchema = z.enum([
  'planning',
  'active',
  'on_hold',
  'completed',
  'cancelled',
]);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

/**
 * Health is reported as a discrete state, never as a color, so that the
 * frontend can pair it with an icon and text label (accessibility, section 18:
 * status must not be communicated by color alone).
 */
export const projectHealthSchema = z.enum(['on_track', 'at_risk', 'off_track', 'unknown']);
export type ProjectHealth = z.infer<typeof projectHealthSchema>;

export const projectSchema = z.object({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  description: z.string().nullable(),
  status: projectStatusSchema,
  health: projectHealthSchema,
  startsOn: z.string().date().nullable(),
  endsOn: z.string().date().nullable(),
  progress: z.number().int().min(0).max(100),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Project = z.infer<typeof projectSchema>;

/**
 * The project a dashboard deployment is about, as the application shell carries
 * it (architecture documentation section 2).
 *
 * Deliberately three fields. The shell needs an identifier to build every
 * project-scoped request from, and a name to say which project the reader is
 * looking at; everything else -- status, health, progress, dates -- is content
 * for the project page, which reads the full record. Keeping the summary thin
 * means a project edit cannot leave a stale figure sitting in the header of
 * every screen until the session context is refetched.
 */
export const projectSummarySchema = projectSchema.pick({ id: true, key: true, name: true });
export type ProjectSummary = z.infer<typeof projectSummarySchema>;

export const projectPageSchema = pageSchema(projectSchema);

export const createProjectSchema = z
  .object({
    key: resourceKeySchema,
    name: z.string().min(1).max(200),
    description: z.string().max(4000).nullish(),
    status: projectStatusSchema.default('planning'),
    health: projectHealthSchema.default('unknown'),
    startsOn: z.string().date().nullish(),
    endsOn: z.string().date().nullish(),
    progress: z.number().int().min(0).max(100).default(0),
  })
  .strict()
  .refine((v) => !v.startsOn || !v.endsOn || v.startsOn <= v.endsOn, {
    message: 'endsOn must not precede startsOn',
    path: ['endsOn'],
  });
export type CreateProject = z.infer<typeof createProjectSchema>;

/**
 * `.strict()` rejects unknown members rather than silently dropping them.
 * That is the mass-assignment control required by section 14.3 and it also
 * surfaces client/server contract drift immediately instead of at read time.
 */
export const updateProjectSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(4000).nullish(),
    status: projectStatusSchema.optional(),
    health: projectHealthSchema.optional(),
    startsOn: z.string().date().nullish(),
    endsOn: z.string().date().nullish(),
    progress: z.number().int().min(0).max(100).optional(),
    /** Value last read by the client; drift yields 412. */
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateProject = z.infer<typeof updateProjectSchema>;

export const projectListQuerySchema = z.object({
  status: projectStatusSchema.optional(),
  health: projectHealthSchema.optional(),
  search: z.string().max(120).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
